/**
 * Admin Orca Container RPC definitions
 *
 * Admin endpoint for listing running orca Docker containers with status.
 *
 * @since 1.0.0
 */
import { Rpc, RpcGroup } from "@effect/rpc";
import { Schema } from "effect";
import { AdminMiddleware } from "../admin.tags.ts";
import { Forbidden, Unauthorized } from "../errors.ts";

// ============================================================================
// Error Schemas
// ============================================================================

/**
 * Error for Docker operations (e.g. docker ps fails)
 * @since 1.0.0
 */
export class OrcaDockerError extends Schema.TaggedError<OrcaDockerError>()(
  "OrcaDockerError",
  {
    message: Schema.String,
  },
) {}

// ============================================================================
// Response Schemas
// ============================================================================

/**
 * Schema for a single running orca container
 * @since 1.0.0
 */
export const OrcaContainer = Schema.Struct({
  containerId: Schema.String,
  name: Schema.String,
  status: Schema.String,
  createdAt: Schema.String,
  taskId: Schema.String,
  issueId: Schema.String,
  agentType: Schema.String,
});

/**
 * TypeScript type for OrcaContainer
 * @since 1.0.0
 */
export type OrcaContainer = Schema.Schema.Type<typeof OrcaContainer>;

/**
 * CLA-398: input for fetching a container's docker logs. `tail` caps how many
 * trailing lines to return (clamped server-side); omitted → a sane default.
 *
 * @since 1.0.0
 */
export const OrcaContainerLogsInput = Schema.Struct({
  containerId: Schema.String,
  tail: Schema.optional(Schema.Number),
});

/**
 * @since 1.0.0
 */
export type OrcaContainerLogsInput = Schema.Schema.Type<typeof OrcaContainerLogsInput>;

/**
 * CLA-398: a container's recent docker logs. Now that builders run off-host on
 * the runner Mac, this is the operator's live tail into a container that has not
 * yet been archived to S3 (see the orca project detail's per-issue archive
 * link for the post-run artifacts).
 *
 * @since 1.0.0
 */
export const OrcaContainerLogs = Schema.Struct({
  containerId: Schema.String,
  logs: Schema.String,
});

/**
 * @since 1.0.0
 */
export type OrcaContainerLogs = Schema.Schema.Type<typeof OrcaContainerLogs>;

// ============================================================================
// RPC Group
// ============================================================================

/**
 * Admin Orca Container RPC group
 *
 * All endpoints require admin role via AdminMiddleware.
 *
 * @since 1.0.0
 */
export class AdminOrcaContainerRpcs extends RpcGroup.make(
  Rpc.make("admin.orcaContainer.list", {
    success: Schema.Array(OrcaContainer),
    error: Schema.Union(Unauthorized, Forbidden, OrcaDockerError),
  }).middleware(AdminMiddleware),
  // CLA-398: fetch a running container's docker logs for operator triage.
  Rpc.make("admin.orcaContainer.logs", {
    payload: OrcaContainerLogsInput,
    success: OrcaContainerLogs,
    error: Schema.Union(Unauthorized, Forbidden, OrcaDockerError),
  }).middleware(AdminMiddleware),
) {}
